/**
 * DOC — 2ª via do boleto do morador (Portal do Condômino) — 0.1.47.
 *
 * O morador vê só o(s) boleto(s) do apartamento dele (casado pelo e-mail do cadastro)
 * e baixa a página em PDF. Avisos bem visíveis: é uma CÓPIA; confira todos os dados;
 * o boleto oficial é o enviado por e-mail pela administradora.
 */
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, Download, FileText, Loader2, ShieldCheck,
} from "lucide-react";
import { api, boletoArquivoUrl } from "../lib/api";

interface MeuBoleto {
  boleto_id: number;
  competencia: string | null;
  tipo: string;
  unidade: string;
  condomino_nome: string | null;
  cpf_mascarado: string | null;
  valor: number | null;
  vencimento: string | null;
  vencido: boolean;
}

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
function competenciaLabel(c: string | null): string {
  if (!c) return "";
  const [a, m] = c.split("-");
  const mi = Number(m) - 1;
  return `${MESES[mi] ?? m}/${a}`;
}
function dataBR(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}
function reais(v: number | null): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function BoletosMoradorPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const { data, isLoading, isError } = useQuery<MeuBoleto[]>({
    queryKey: ["meus-boletos", condominioId],
    queryFn: () => api.get<MeuBoleto[]>(`/condominios/${condominioId}/boletos/meu`),
  });

  return (
    <div className="max-w-lg mx-auto px-3 py-4">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <FileText size={20} className="text-brand-700" /> Meus boletos (DOC)
        </h1>
      </div>

      {/* Aviso: cópia / conferência */}
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-4 flex gap-2">
        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
        <div>
          <b>Esta é uma cópia</b> do boleto enviado por e-mail pela administradora — para sua comodidade.
          <b> Confira todos os dados</b> (valor, vencimento, unidade e seu nome) antes de pagar.
          O documento oficial é o enviado diretamente a você pela administradora.
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Carregando…</p>
      )}
      {isError && <p className="text-sm text-red-700">Não foi possível carregar seus boletos agora.</p>}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
          Nenhum boleto disponível para o seu apartamento no momento.
          <div className="mt-1 text-slate-500">
            Se você paga <b>antecipado</b>, foi cadastrado agora há pouco, ou o síndico ainda não publicou o mês,
            o boleto pode não aparecer aqui. Nesse caso, use o boleto que a administradora enviou por e-mail
            ou fale com o síndico.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {data?.map((b) => (
          <div key={b.boleto_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  {b.tipo === "EXTRA" ? "Chamada extra" : "Mensal"} · {b.unidade}
                </div>
                <div className="text-lg font-bold text-slate-900">{competenciaLabel(b.competencia)}</div>
              </div>
              {b.vencido
                ? <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700">Vencido</span>
                : <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">Em dia</span>}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-y-1 text-sm">
              <span className="text-slate-500">Valor</span>
              <span className="text-right font-semibold text-slate-900">{reais(b.valor)}</span>
              <span className="text-slate-500">Vencimento</span>
              <span className="text-right text-slate-900">{dataBR(b.vencimento)}</span>
              {b.condomino_nome && (<><span className="text-slate-500">Sacado</span>
                <span className="text-right text-slate-900 truncate">{b.condomino_nome}</span></>)}
              {b.cpf_mascarado && (<><span className="text-slate-500">CPF/CNPJ</span>
                <span className="text-right text-slate-900">{b.cpf_mascarado}</span></>)}
            </div>

            <div className="mt-2 text-[12px] text-slate-500 flex items-center gap-1">
              <ShieldCheck size={13} className="text-green-600" />
              Confira se o final do seu CPF/CNPJ e a unidade estão corretos.
            </div>

            <a href={boletoArquivoUrl(b.boleto_id)} target="_blank" rel="noreferrer"
              className="mt-3 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-brand-700 hover:bg-brand-800 text-white font-semibold">
              <Download size={18} /> Baixar meu boleto (PDF)
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
